import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { debounceTime, map, shareReplay, tap } from "rxjs/operators";
import {
    AircraftService,
    AssessmentService,
    StatsService,
    USER_IS_AT_LEAST_ADMIN,
    UserService,
} from "@/_services";
import { FilterService } from "@/_services/filter.service";
import { FLIGHT_STATUS_MAP, MitigationStats, StatsResult } from "@/_models";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import moment from "moment";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { getTimeFrameList } from "@/_models/filters/default-time-frame-list";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
    buildStatsFilterDefaults,
    mergeFilterDefaults,
} from "@/_models/filters/shared-assessment-filters";

@UntilDestroy()
@Component({
    selector: "app-assessment-statistics",
    templateUrl: "./assessment-statistics.component.html",
    styleUrls: ["./assessment-statistics.component.scss"],
})
export class AssessmentStatisticsComponent implements OnInit {
    stats$: Observable<StatsResult>;
    statsData$: Observable<MitigationStats>;
    assessmentId: string;
    assessmentTitle$: Observable<string>;
    filterForm: UntypedFormGroup;
    multipleStatsData$: Observable<MitigationStats[]>;
    isSingleStatMode = false;
    currentFilters = null;

    statusList = FLIGHT_STATUS_MAP;

    timeFrameList: RadioOption[] = getTimeFrameList(["All"]);

    userList$: Observable<RadioOption[]>;
    aircraftList$: Observable<RadioOption[]>;

    private filterFormSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private assessmentService: AssessmentService,
        private filterService: FilterService,
        private userService: UserService,
        private statsService: StatsService,
        private aircraftService: AircraftService,
        private formBuilder: UntypedFormBuilder,
        @Inject(USER_IS_AT_LEAST_ADMIN) public isAdmin$: Observable<boolean>,
    ) {}

    ngOnInit(): void {
        this.assessmentId = this.route.parent?.snapshot.params["assessmentId"];

        if (this.assessmentId) {
            this.loadStats();
            this.loadAssessmentTitle();
            this.loadFilterData();
        }
    }

    loadStats() {
        const today = moment().format("YYYY-MM-DD");
        const oneYearAgo = moment().subtract(365, "days").format("YYYY-MM-DD");

        const localStorageKey = LOCAL_STORAGE_KEYS.ASSESSMENT_FILTERS(
            this.assessmentId,
        );

        const defaults = buildStatsFilterDefaults(this.assessmentId, [
            oneYearAgo,
            today,
        ]);
        const persisted = this.filterService.loadSimpleFilters(
            localStorageKey,
            {},
        );
        const mergedFilters = mergeFilterDefaults(defaults, persisted);

        this.filterForm = this.formBuilder.group({
            status: [mergedFilters.status],
            showArchived: [mergedFilters.showArchived],
            responsiblePilot: [mergedFilters.responsiblePilot],
            timeFrame: [mergedFilters.timeFrame],
            flightDate: [mergedFilters.flightDate],
            aircraft: [mergedFilters.aircraft],
            airport: [mergedFilters.airport],
            assessment: [mergedFilters.assessment],
            formType: [mergedFilters.formType],
        });

        this.filterFormSubscription = this.filterForm.valueChanges
            .pipe(
                untilDestroyed(this),
                debounceTime(500),
                tap((changes) => {
                    this.currentFilters = { ...changes };

                    this.filterService.saveSimpleFilters(
                        localStorageKey,
                        changes,
                    );

                    this.updateFlightList();
                }),
            )
            .subscribe();

        this.filterForm.updateValueAndValidity({ emitEvent: true });

        this.currentFilters = { ...this.filterForm.value };

        this.filterService.saveSimpleFilters(
            localStorageKey,
            this.currentFilters,
        );
    }

    updateFlightList() {
        this.stats$ = this.statsService
            .getStats(this.filterForm.value)
            .pipe(shareReplay());

        this.statsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? null : stats.data;
                this.isSingleStatMode = !!result;
                return result;
            }),
            shareReplay(),
        );

        this.multipleStatsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? stats.data : null;
                return result;
            }),
            shareReplay(),
        );
    }

    loadAssessmentTitle() {
        this.assessmentTitle$ = this.assessmentService
            .getAssessment(this.assessmentId)
            .pipe(map((assessment) => assessment?.title || "Assessment"));
    }

    private loadFilterData() {
        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => {
                    return { label: user.fullName, value: user._id };
                }),
            ),
            shareReplay(),
        );

        this.aircraftList$ = this.aircraftService.listAircraft().pipe(
            map((aircrafts) =>
                aircrafts.map((aircraft) => {
                    return { label: aircraft.designation, value: aircraft._id };
                }),
            ),
            shareReplay(),
        );
    }
}
